import { captureException } from '@sentry/react';
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type OrderModel,
  orderClient,
  type SearchOrderFilterModel,
  type UpdateOrderModel,
} from '@merchant-clients/order';

import { useToast } from '@frontend/components';
import { useClientQuery } from '@frontend/hooks';
import { tApp, tOrder } from '@frontend/locales';

const fetchOrders = async (filter: SearchOrderFilterModel) => {
  return orderClient.list({
    filter,
  });
};

const applyPreOrderFilter = (
  orders: OrderModel[],
  preOrderFilter: number | undefined,
): OrderModel[] => {
  if (!preOrderFilter) {
    return orders;
  }

  const now = new Date();
  const preOrderDate = new Date(now.getTime() + preOrderFilter * 60000);

  const preOrderFiltered = orders.filter((order) => {
    if (!order.timeRequested) {
      return true;
    }

    return new Date(order.timeRequested) < preOrderDate;
  });

  return preOrderFiltered;
};

export const getOrdersQueryKey = (shopId: string) => ['orders', shopId];

export interface UseOrdersQueryProps {
  filter: SearchOrderFilterModel;
  preOrder: number | undefined;
  onError?(error: Error): void;
}

export const useOrdersQuery = ({
  filter,
  preOrder,
  onError,
}: UseOrdersQueryProps) => {
  return useClientQuery({
    queryKey: getOrdersQueryKey(filter.shopId),
    enabled: !!filter.shopId,
    async onFetch() {
      const orders = await fetchOrders({
        period: filter.period,
        placeIds: filter.placeIds,
        status: filter.status,
        shopId: filter.shopId,
        orderIds: filter.orderIds,
        verificationIds: filter.verificationIds,
      });

      return applyPreOrderFilter(orders, preOrder);
    },
    onError,
    refetchInterval: 10000,
  });
};

const updateCachedOrder = (
  queryClient: QueryClient,
  shopId: string,
  orderId: string,
  updateFn: (existingOrder: OrderModel) => OrderModel,
) => {
  queryClient.setQueryData<OrderModel[]>(
    getOrdersQueryKey(shopId),
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      const updatedOrders = oldData.map((order: OrderModel) => {
        if (order.id !== orderId) {
          return order;
        }

        return updateFn(order);
      });

      return updatedOrders;
    },
  );
};

export const performOrderStatusUpdate = async (
  queryClient: QueryClient,
  shopId: string,
  orderId: string,
  status: UpdateOrderModel['status'],
): Promise<OrderModel | undefined> => {
  const existingOrderData = queryClient.getQueryData<OrderModel[]>(
    getOrdersQueryKey(shopId),
  );

  const existingOrder = existingOrderData?.find(
    (order) => order.id === orderId,
  );

  if (!existingOrder) {
    captureException(
      new Error(
        `Order with id ${orderId} not found during update of order status, ignoring update`,
      ),
      {
        level: 'warning',
      },
    );

    return undefined;
  }

  updateCachedOrder(queryClient, shopId, orderId, (order) => ({
    ...order,
    loadingState: true,
  }));

  try {
    const updatedOrder = await orderClient.update(orderId, {
      shopId,
      status,
    });

    // The update API is synchronous, so the response already reflects the
    // final order state and can be written straight into the cache.
    updateCachedOrder(queryClient, shopId, orderId, () => updatedOrder);

    return updatedOrder;
  } catch (error) {
    updateCachedOrder(queryClient, shopId, orderId, () => existingOrder);
    throw error;
  }
};

export const useUpdateOrderMutation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    OrderModel | undefined,
    Error,
    {
      shopId: string;
      orderId: string;
      status: UpdateOrderModel['status'];
    }
  >({
    mutationFn: ({ shopId, orderId, status }) =>
      performOrderStatusUpdate(queryClient, shopId, orderId, status),
    onError(error, { orderId }, _context) {
      showToast({
        header: tOrder.actions.statusUpdateError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to reject order with id ${orderId}`,
        },
      });
    },
  });
};
